import { CommentOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import type { SatisfactionRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Rate,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../session';
import { TipText, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const EVAL_TYPE_META: Record<string, { text: string; color: string }> = {
  DELIVERY: { text: '配送服务', color: 'blue' },
  MATERIAL: { text: '商品质量', color: 'geekblue' },
  AFTER_SALE: { text: '售后服务', color: 'orange' },
  PLATFORM: { text: '平台体验', color: 'purple' }
};

const STATUS_META: Record<string, { text: string; color: string }> = {
  SUBMITTED: { text: '待回复', color: 'orange' },
  REPLIED: { text: '已回复', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' }
};

const percent = (value: number | undefined): string => `${((value ?? 0) * 100).toFixed(1)}%`;

/**
 * 满意度评价（`sv_satisfaction`）：配送服务 / 商品质量 / 售后服务 / 平台体验四类打分。
 * 客服在评价下回复即计入「实时售后服务回复」举证，并自动联动该评价的客服会话（docs/03 §K1）。
 */
export default function ServiceSatisfaction() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [evalType, setEvalType] = useState<string>('ALL');
  const [status, setStatus] = useState<string>('ALL');
  const [replyTarget, setReplyTarget] = useState<SatisfactionRow | null>(null);
  const [replyDraft, setReplyDraft] = useState('');

  const list = useInfiniteQuery({
    queryKey: ['ops', 'satisfaction', 'list', evalType, status],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.satisfaction.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        evalType: evalType === 'ALL' ? undefined : evalType,
        status: status === 'ALL' ? undefined : status
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const stats = useQuery({
    queryKey: ['ops', 'satisfaction', 'stats'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.satisfaction.stats()
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'satisfaction'] });
  };

  const reply = useMutation({
    mutationFn: (payload: { id: string; content: string }) => hq.satisfaction.reply(payload.id, payload.content),
    onSuccess: async (result) => {
      message.success(`已回复，已联动客服会话 ${result.threadNo}`);
      setReplyTarget(null);
      setReplyDraft('');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '回复失败')
  });

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const goodRate = stats.data?.goodRate ?? 0;

  const columns: ColumnsType<SatisfactionRow> = [
    { title: '评价时间', dataIndex: 'evalAt', width: 150, render: (value: string) => formatDisplayTime(value) },
    {
      title: '类型',
      dataIndex: 'evalType',
      width: 110,
      render: (value: string) => {
        const meta = EVAL_TYPE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '科室', dataIndex: 'deptName', width: 140, render: (value: string | null) => value || '-' },
    {
      title: '被评价方',
      dataIndex: 'supplierName',
      width: 180,
      ellipsis: { showTitle: false },
      render: (value: string | null, row) => <TipText text={value || (row.evalType === 'PLATFORM' ? '平台' : '-')} />
    },
    {
      title: '评分',
      dataIndex: 'scoreTotal',
      width: 190,
      render: (value: number) => (
        <Space size={6}>
          <Rate disabled allowHalf value={Number(value)} style={{ fontSize: 14 }} />
          <Typography.Text type="secondary">{Number(value).toFixed(1)}</Typography.Text>
        </Space>
      )
    },
    {
      title: '评价内容',
      dataIndex: 'content',
      ellipsis: { showTitle: false },
      render: (value: string | null, row) =>
        row.tags
          ? <TipText text={value || '[未填写文字评价]'} tip={`${value || '[未填写文字评价]'}（标签：${row.tags}）`} />
          : <TipText text={value || '[未填写文字评价]'} />
    },
    {
      title: '评价人',
      dataIndex: 'evalUserName',
      width: 130,
      render: (value: string | null, row) =>
        row.anonFlag ? <Tag>匿名</Tag> : value || '-'
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => (
        <Tag color={STATUS_META[value]?.color ?? 'default'}>{STATUS_META[value]?.text ?? value}</Tag>
      )
    },
    { title: '回复时间', dataIndex: 'replyAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={row.status === 'CLOSED'}
            onClick={() => {
              setReplyTarget(row);
              setReplyDraft(row.replyContent ?? '');
            }}
          >
            {row.status === 'REPLIED' ? '追评' : '回复'}
          </Button>
          <Button
            type="link"
            onClick={() => navigate(`/service?thread=${encodeURIComponent(`SV_SATISFACTION-${row.id}`)}`)}
          >
            会话
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="满意度评价"
      extra={
        <Button
          icon={<ReloadOutlined />}
          loading={list.isFetching}
          onClick={() => {
            void list.refetch();
            void stats.refetch();
          }}
        >
          刷新
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">
        四类评价（配送服务 / 商品质量 / 售后服务 / 平台体验），1–5 分制；客服回复即计入「实时售后服务回复」举证，
        并自动在该评价对应的客服会话中留痕（招标 ★2(3)）。
      </Typography.Paragraph>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic title="评价总数" value={stats.data?.count ?? 0} />
        </Col>
        <Col span={6}>
          <Statistic
            title="平均评分"
            value={Number(stats.data?.avgScore ?? 0)}
            precision={2}
            suffix="/ 5"
            valueStyle={{ color: Number(stats.data?.avgScore ?? 0) >= 4 ? '#3f8600' : '#cf1322' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="好评率（≥4 分）"
            value={goodRate * 100}
            precision={1}
            suffix="%"
            valueStyle={{ color: goodRate >= 0.9 ? '#3f8600' : '#cf1322' }}
          />
        </Col>
        <Col span={6}>
          <Statistic title="回复率" value={percent(stats.data?.replyRate)} />
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented<string>
          value={evalType}
          options={[
            { label: '全部类型', value: 'ALL' },
            { label: '配送服务', value: 'DELIVERY' },
            { label: '商品质量', value: 'MATERIAL' },
            { label: '售后服务', value: 'AFTER_SALE' },
            { label: '平台体验', value: 'PLATFORM' }
          ]}
          onChange={(value) => setEvalType(value)}
        />
        <Select<string>
          value={status}
          style={{ width: 140 }}
          onChange={(value) => setStatus(value)}
          options={[
            { value: 'ALL', label: '全部状态' },
            { value: 'SUBMITTED', label: '待回复' },
            { value: 'REPLIED', label: '已回复' },
            { value: 'CLOSED', label: '已关闭' }
          ]}
        />
      </Space>

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="满意度评价加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<SatisfactionRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无满意度评价' }}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Modal
        title="回复评价"
        open={replyTarget !== null}
        onCancel={() => setReplyTarget(null)}
        width={680}
        confirmLoading={reply.isPending}
        okText="提交回复"
        okButtonProps={{ icon: <SendOutlined />, disabled: replyDraft.trim().length < 2 }}
        onOk={() => {
          if (!replyTarget) return;
          reply.mutate({ id: replyTarget.id, content: replyDraft.trim() });
        }}
      >
        {replyTarget ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="类型">
                <Tag color={EVAL_TYPE_META[replyTarget.evalType]?.color ?? 'default'}>
                  {EVAL_TYPE_META[replyTarget.evalType]?.text ?? replyTarget.evalType}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="评分">
                <Space size={6}>
                  <Rate disabled allowHalf value={Number(replyTarget.scoreTotal)} style={{ fontSize: 14 }} />
                  <Typography.Text type="secondary">{Number(replyTarget.scoreTotal).toFixed(1)}</Typography.Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="科室">{replyTarget.deptName || '-'}</Descriptions.Item>
              <Descriptions.Item label="被评价方">
                {replyTarget.supplierName || (replyTarget.evalType === 'PLATFORM' ? '平台' : '-')}
              </Descriptions.Item>
              <Descriptions.Item label="评价人">
                {replyTarget.anonFlag ? '匿名评价' : replyTarget.evalUserName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="评价时间">{formatDisplayTime(replyTarget.evalAt)}</Descriptions.Item>
              <Descriptions.Item label="评价内容" span={2}>
                {replyTarget.content || '[未填写文字评价]'}
                {replyTarget.tags ? (
                  <Typography.Text type="secondary">（标签：{replyTarget.tags}）</Typography.Text>
                ) : null}
              </Descriptions.Item>
            </Descriptions>
            <Input.TextArea
              rows={4}
              maxLength={1000}
              showCount
              placeholder="回复内容将同步到该评价的客服会话，并作为 24h 内响应举证"
              value={replyDraft}
              onChange={(event) => setReplyDraft(event.target.value)}
            />
            {replyTarget.replyContent ? (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                message="最近一次回复"
                description={
                  <Space direction="vertical" size={0}>
                    <Typography.Text>{replyTarget.replyContent}</Typography.Text>
                    <Typography.Text type="secondary">{formatDisplayTime(replyTarget.replyAt)}</Typography.Text>
                  </Space>
                }
              />
            ) : null}
            <Space style={{ marginTop: 12 }}>
              <Button
                icon={<CommentOutlined />}
                onClick={() =>
                  navigate(`/service?thread=${encodeURIComponent(`SV_SATISFACTION-${replyTarget.id}`)}`)
                }
              >
                打开客服会话
              </Button>
            </Space>
          </>
        ) : null}
      </Modal>
    </Card>
  );
}
